"use client";

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatRatio } from "@/lib/format";

export interface MatrixPoint {
  symbol: string;
  name: string;
  /** Polaritaets-korrigierter Z-Score der Bewertungskennzahl (KGV/EV-EBITDA) - positiv = guenstig. */
  x: number;
  /** Polaritaets-korrigierter Z-Score der Qualitaetskennzahl (ROIC/Nettomarge) - positiv = hohe Qualitaet. */
  y: number;
  rawValueMetric: number | undefined;
  rawQualityMetric: number | undefined;
  isOwn: boolean;
}

/** 4-Quadranten Quality/Value-Matrix (siehe backlog.md -> "Institutionelle
 * Peer-Group-Engine", Punkt 3). Beide Achsen sind bereits Z-Scores relativ zur
 * Peer-Gruppe (siehe lib/peer-group/engine.ts) - dadurch liegt der Nullpunkt immer
 * exakt auf dem Gruppen-Median und die vier Quadranten sind ohne zusaetzliche
 * Achsenbereich-Berechnung eindeutig ("guenstig & hochprofitabel" = oben rechts).
 * Bewusst im bestehenden hellen App-Design statt eigener dunkler Optik, auf
 * Eriks Wunsch konsistent mit dem restlichen Benchmark-Tab. */
export function QualityValueMatrix({
  points,
  valueLabel,
  qualityLabel,
}: {
  points: MatrixPoint[];
  valueLabel: string;
  qualityLabel: string;
}) {
  const domainMax = Math.max(2.5, ...points.map((p) => Math.max(Math.abs(p.x), Math.abs(p.y)))) + 0.3;
  const domain: [number, number] = [-domainMax, domainMax];

  return (
    <div className="rounded-lg border border-hairline p-4">
      <div className="mb-1 text-sm font-medium text-ink-secondary">Quality/Value-Matrix</div>
      <p className="mb-3 text-xs text-muted-foreground">
        {qualityLabel} (Y) vs. {valueLabel}, günstig → (X) — jeweils relativ zur Peer-Gruppe.
      </p>
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <ReferenceArea x1={0} y1={0} x2={domainMax} y2={domainMax} fill="#3F7A5B" fillOpacity={0.07} />
            <ReferenceLine x={0} stroke="#ECEAE3" />
            <ReferenceLine y={0} stroke="#ECEAE3" />
            <XAxis
              type="number"
              dataKey="x"
              domain={domain}
              tick={{ fontSize: 11, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
              tickFormatter={() => ""}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={domain}
              tick={{ fontSize: 11, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
              tickFormatter={() => ""}
            />
            <ZAxis type="number" range={[70, 240]} />
            <Tooltip
              cursor={{ strokeDasharray: "3 3", stroke: "#ECEAE3" }}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload as MatrixPoint;
                return (
                  <div
                    className="rounded-md border bg-white px-3 py-2 text-xs shadow-md"
                    style={{ borderColor: "#ECEAE3" }}
                  >
                    <div className="font-medium text-ink">
                      {p.name} ({p.symbol})
                    </div>
                    <div className="mt-1 text-muted-foreground">
                      {valueLabel}: {formatRatio(p.rawValueMetric)} · {qualityLabel}:{" "}
                      {p.rawQualityMetric !== undefined ? `${(p.rawQualityMetric * 100).toFixed(1)} %` : "–"}
                    </div>
                  </div>
                );
              }}
            />
            <Scatter data={points} shape="circle">
              {points.map((p) => (
                <Cell
                  key={p.symbol}
                  fill={p.isOwn ? "#16265C" : "#C6A15B"}
                  fillOpacity={p.isOwn ? 1 : 0.75}
                  r={p.isOwn ? 8 : 5}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>← teuer</span>
        <span>günstig →</span>
      </div>
    </div>
  );
}
